import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { trpc } from '@/lib/trpc';
import { FAVORITE_WORKSPACES_KEY } from '@/lib/consts';
import type { RouterOutputs } from '@/lib/trpc';

type WorkspaceWithChats = RouterOutputs['chat']['listWorkspaces']['workspaces'][number];

export function useFavoriteWorkspaces() {
  const [favoriteIds, setFavoriteIds] = useLocalStorage<string[]>(FAVORITE_WORKSPACES_KEY, []);

  const query = trpc.chat.getWorkspacesByIds.useQuery(
    { ids: favoriteIds },
    {
      enabled: favoriteIds.length > 0,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );

  const toggleFavorite = useCallback(
    (workspaceId: string) => {
      setFavoriteIds((prev) =>
        prev.includes(workspaceId)
          ? prev.filter((id) => id !== workspaceId)
          : [...prev, workspaceId]
      );
    },
    [setFavoriteIds]
  );

  const isFavorite = useCallback(
    (workspaceId: string) => favoriteIds.includes(workspaceId),
    [favoriteIds]
  );

  const favoriteIdsSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  return {
    favoriteIds,
    favoriteIdsSet,
    toggleFavorite,
    isFavorite,
    favoriteWorkspaces: (query.data?.workspaces ?? []) as WorkspaceWithChats[],
    isLoading: query.isLoading && favoriteIds.length > 0,
  };
}
